"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { PixelText } from "@/components/ui/pixel-text";
import { LogIn } from "lucide-react";

export default function LoginPage() {
  const router = useRouter();
  const setUser = useAuthStore((s) => s.setUser);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      const data = await res.json();
      console.log("[login] status:", res.status, "data:", JSON.stringify(data));
      console.log("[login] Set-Cookie header:", res.headers.get("set-cookie"));

      if (!res.ok) {
        setError(data.error || "Login failed");
        return;
      }

      setUser(data.user);
      console.log("[login] user set, team:", data.user?.team, "pushing to game");
      if (!data.user?.team) {
        router.push("/onboarding");
      } else {
        router.push("/game");
      }
    } catch (err) {
      console.error("[login] error:", err);
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="p-8">
      <div className="text-center mb-8">
        <PixelText as="h1" glow="pink" size="xl" className="mb-2">
          SURGGIES
        </PixelText>
        <p className="text-[var(--ink-soft)] font-mono text-sm">
          Log in to your empire
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-[var(--ink-soft)] font-mono text-xs mb-1.5 uppercase tracking-wider">
            Email
          </label>
          <Input
            type="email"
            placeholder="ceo@surggies.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-[var(--ink-soft)] font-mono text-xs mb-1.5 uppercase tracking-wider">
            Password
          </label>
          <Input
            type="password"
            placeholder="********"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && (
          <p className="text-[#c54a4a] font-mono text-xs text-center">{error}</p>
        )}

        <Button type="submit" className="w-full" size="lg" disabled={loading}>
          <LogIn className="w-4 h-4 mr-2" />
          {loading ? "Logging in..." : "Enter Game"}
        </Button>
      </form>

      <p className="text-center text-[var(--ink-soft)] font-mono text-xs mt-6">
        No account?{" "}
        <Link
          href="/register"
          className="text-[var(--gold-deep)] hover:text-[var(--ink)] underline underline-offset-2"
        >
          Register here
        </Link>
      </p>
    </Card>
  );
}
